import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { isDemandFailure, requestDemandDiagnosis } from '../almaDemandApi';
import {
  DIFFICULTY_MAX_LENGTH,
  EMPTY_DEMAND_FORM,
  SALES_HISTORY_OPTIONS,
  SEGMENT_OPTIONS,
  SKU_OPTIONS,
  STOCK_CONTROL_OPTIONS,
  STORE_OPTIONS,
} from '../types';
import { FIELD_ORDER, validateDemandForm } from '../validation';
import type {
  DemandFormData,
  DemandFormErrors,
  DemandFormField,
  DemandRequestState,
  SelectOption,
} from '../types';

const inputClass = (hasError: boolean) =>
  `w-full border rounded-lg px-3.5 py-3 text-sm font-inter text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors ${
    hasError ? 'border-red-400' : 'border-zinc-200'
  }`;

const labelClass = 'block font-inter text-xs font-medium text-zinc-700 mb-1.5';

export default function RequestFormSection() {
  const [formData, setFormData] = useState<DemandFormData>(EMPTY_DEMAND_FORM);
  const [errors, setErrors] = useState<DemandFormErrors>({});
  const [status, setStatus] = useState<DemandRequestState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  function focusFirstInvalid(nextErrors: DemandFormErrors) {
    const firstField = FIELD_ORDER.find((field) => nextErrors[field]);
    if (!firstField || !formRef.current) return;
    const element = formRef.current.querySelector<HTMLElement>(
      `[name="${firstField}"]`
    );
    element?.focus();
  }

  function handleChange(
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) {
    const { name, value, type, checked } = event.target as HTMLInputElement;
    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const nextErrors = validateDemandForm(formData);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      focusFirstInvalid(nextErrors);
      return;
    }

    setErrors({});
    setErrorMessage('');
    setStatus('loading');

    const result = await requestDemandDiagnosis(formData);

    if (isDemandFailure(result)) {
      setStatus('error');
      setErrorMessage(
        result.message ?? 'Não foi possível enviar a solicitação. Tente novamente.'
      );
      if (result.fieldErrors) setErrors(result.fieldErrors);
      return;
    }

    setStatus('success');
  }

  function renderError(field: DemandFormField) {
    if (!errors[field]) return null;
    return (
      <p id={`${field}-error`} className="text-red-500 text-xs mt-1 font-inter">
        {errors[field]}
      </p>
    );
  }

  function describedBy(field: DemandFormField) {
    return errors[field] ? `${field}-error` : undefined;
  }

  function renderSelect(
    field: DemandFormField,
    label: string,
    options: SelectOption[]
  ) {
    return (
      <div>
        <label className={labelClass} htmlFor={field}>
          {label} *
        </label>
        <select
          id={field}
          name={field}
          value={formData[field] as string}
          onChange={handleChange}
          aria-invalid={Boolean(errors[field])}
          aria-describedby={describedBy(field)}
          className={inputClass(Boolean(errors[field]))}
        >
          <option value="">Selecione</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {renderError(field)}
      </div>
    );
  }

  return (
    <section id="solicitar" className="py-20 md:py-28 bg-zinc-50">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block font-sora font-semibold text-emerald-600 text-sm uppercase tracking-widest mb-3">
            Solicitar diagnóstico
          </span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
            Comece pelo seu histórico
          </h2>
          <p className="font-inter text-zinc-500 max-w-xl mx-auto text-base">
            Preencha os dados abaixo para solicitar o diagnóstico. Nossa equipe
            responde em até 1 dia útil com os próximos passos.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        <div className="bg-white rounded-2xl border border-zinc-100 p-6 md:p-8">
          <div aria-live="polite">
            {status === 'success' && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div
                  className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-5"
                  aria-hidden="true"
                >
                  <i className="ri-check-line text-emerald-600 text-3xl" />
                </div>
                <h3 className="font-sora font-bold text-xl text-zinc-900 mb-3">
                  Solicitação recebida
                </h3>
                <p className="font-inter text-zinc-500 text-sm mb-6 max-w-sm">
                  Nossa equipe vai analisar as informações e retornar em até 1 dia
                  útil com os próximos passos para o envio do histórico.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(EMPTY_DEMAND_FORM);
                    setStatus('idle');
                  }}
                  className="text-emerald-600 font-semibold text-sm font-sora cursor-pointer hover:text-emerald-700 transition-colors"
                >
                  Enviar outra solicitação
                </button>
              </div>
            )}
          </div>

          {status !== 'success' && (
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              <div aria-live="polite">
                {status === 'error' && errorMessage && (
                  <div
                    role="alert"
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-inter text-sm"
                  >
                    {errorMessage}
                  </div>
                )}
              </div>

              <h3 className="font-sora font-bold text-lg text-zinc-900 mb-4">
                Identificação
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div>
                  <label className={labelClass} htmlFor="full_name">
                    Nome *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Seu nome completo"
                    aria-invalid={Boolean(errors.full_name)}
                    aria-describedby={describedBy('full_name')}
                    className={inputClass(Boolean(errors.full_name))}
                  />
                  {renderError('full_name')}
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@empresa.com"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                    className={inputClass(Boolean(errors.email))}
                  />
                  {renderError('email')}
                </div>
                <div>
                  <label className={labelClass} htmlFor="company">
                    Empresa *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Nome da sua empresa"
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={describedBy('company')}
                    className={inputClass(Boolean(errors.company))}
                  />
                  {renderError('company')}
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone">
                    Telefone / WhatsApp{' '}
                    <span className="text-zinc-400 font-normal">(opcional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+55 (11) 99999-9999"
                    className={inputClass(false)}
                  />
                </div>
              </div>

              <h3 className="font-sora font-bold text-lg text-zinc-900 mb-4">
                Sobre a sua operação
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {renderSelect('segment', 'Segmento', SEGMENT_OPTIONS)}
                {renderSelect('stores', 'Lojas ou unidades', STORE_OPTIONS)}
                {renderSelect('skus', 'Produtos ativos (SKUs)', SKU_OPTIONS)}
                {renderSelect(
                  'sales_history',
                  'Histórico de vendas disponível',
                  SALES_HISTORY_OPTIONS
                )}
              </div>
              <div className="mb-4">
                {renderSelect(
                  'stock_control',
                  'Como controla estoque e compras hoje',
                  STOCK_CONTROL_OPTIONS
                )}
              </div>

              <div className="mb-6">
                <label className={labelClass} htmlFor="difficulty">
                  Principal dificuldade{' '}
                  <span className="text-zinc-400 font-normal">(opcional)</span>{' '}
                  <span className="text-zinc-400 font-normal">
                    ({formData.difficulty.length}/{DIFFICULTY_MAX_LENGTH})
                  </span>
                </label>
                <textarea
                  id="difficulty"
                  name="difficulty"
                  rows={4}
                  maxLength={DIFFICULTY_MAX_LENGTH}
                  value={formData.difficulty}
                  onChange={handleChange}
                  placeholder="Ex.: não sei o que comprar nem quando; tenho produtos parados; perco venda por falta de estoque..."
                  aria-invalid={Boolean(errors.difficulty)}
                  aria-describedby={describedBy('difficulty')}
                  className={inputClass(Boolean(errors.difficulty))}
                />
                {renderError('difficulty')}
              </div>

              <div className="mb-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent_accepted"
                    name="consent_accepted"
                    checked={formData.consent_accepted}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.consent_accepted)}
                    aria-describedby={describedBy('consent_accepted')}
                    className="mt-0.5 w-4 h-4 shrink-0 rounded border-zinc-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500/30 cursor-pointer"
                  />
                  <label
                    htmlFor="consent_accepted"
                    className="font-inter text-xs text-zinc-600 leading-relaxed cursor-pointer"
                  >
                    Autorizo o uso dos dados informados para contato e avaliação do
                    diagnóstico. Leia nossa{' '}
                    <a
                      href="/privacy-policy"
                      className="text-emerald-600 underline hover:text-emerald-700"
                    >
                      política de privacidade
                    </a>
                    .
                  </label>
                </div>
                {renderError('consent_accepted')}
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-semibold font-sora text-base transition-all cursor-pointer whitespace-nowrap"
              >
                {status === 'loading' ? 'Enviando...' : 'Solicitar diagnóstico'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
